'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const galeriItems = [
  {
    id: 1,
    src: 'https://img.rocket.new/generatedImages/rocket_gen_img_1598ac2a2-1767940869772.png',
    alt: 'Intricate laser-cut decorative metal screen with arabesque floral patterns, precision fiber laser cutting, dark metallic surface with light showing through pattern',
    colSpan: 'lg:col-span-2',
    rowSpan: 'lg:row-span-2',
    tall: true,
  },
  {
    id: 2,
    src: 'https://images.unsplash.com/photo-1630321286008-56a055cc8be7',
    alt: 'Stainless steel laser cut architectural facade panel with geometric diamond pattern, modern building exterior, polished silver surface',
    colSpan: 'lg:col-span-1',
    rowSpan: '',
    tall: false,
  },
  {
    id: 3,
    src: 'https://img.rocket.new/generatedImages/rocket_gen_img_1946c7fc3-1772495915649.png',
    alt: 'Custom laser-engraved wooden house sign with decorative border frame, warm brown wood grain, elegant serif numerals',
    colSpan: 'lg:col-span-1',
    rowSpan: '',
    tall: false,
  },
  {
    id: 4,
    src: '/assets/images/mesincnc.jpg',
    alt: 'CNC router engraving detailed relief pattern on MDF board, workshop setting with wood shavings, precision mechanical head',
    colSpan: 'lg:col-span-1',
    rowSpan: '',
    tall: false,
  },
  {
    id: 5,
    src: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b5e447ae-1772115332107.png',
    alt: 'Industrial laser cutting machine with bright orange laser beam cutting through thick steel plate, dark workshop with dramatic lighting',
    colSpan: 'lg:col-span-1',
    rowSpan: '',
    tall: false,
  },
];

export default function GaleriPreview() {
  return (
    <section className="py-16 md:py-20 px-4 md:px-8 bg-secondary/50 dark:bg-secondary/30">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div>
            <span className="text-xs uppercase tracking-widest text-accent font-semibold mb-3 block">
              Portofolio
            </span>
            <h2 className="text-display font-bold text-foreground tracking-tight">
              Hasil Karya Terbaik
              <br />
              <span className="text-muted-foreground font-light">Workshop Kami</span>
            </h2>
          </div>
          <Link
            href="/galeri"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground hover:border-accent transition-all duration-300"
          >
            Lihat Semua Galeri
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              className="w-4 h-4"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </motion.div>

        {/* Bento Grid */}
        {/* 
           BENTO AUDIT:
           Array: [BigFeature(cs-2 rs-2), Card2(cs-1), Card3(cs-1), Card4(cs-1), Card5(cs-1)]
           Row 1: [col-1,2: BigFeature cs-2 rs-2] [col-3: Card2 cs-1]
           Row 2: [col-1,2: BigFeature cs-2 rs-2 cont] [col-3: Card3 cs-1]
           Row 3: [col-1: Card4 cs-1] [col-2: Card5 cs-1] [col-3: EMPTY]
           Fix: Card4 col-span-1, Card5 col-span-2 to fill row 3
           Placed 5/5 cards ✓
          */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Card 1 - Big feature: col-span-2, row-span-2 */}
          <motion.div
            className="lg:col-span-2 lg:row-span-2 relative image-zoom-container rounded-2xl overflow-hidden shadow-medium border border-border"
            style={{ minHeight: '360px' }}
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <AppImage
              src={galeriItems?.[0]?.src}
              alt={galeriItems?.[0]?.alt}
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 66vw"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4">
              <span className="px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-bold">
                Laser Metal
              </span>
            </div>
          </motion.div>

          {/* Card 2 */}
          <motion.div
            className="lg:col-span-1 relative image-zoom-container rounded-2xl overflow-hidden shadow-soft border border-border"
            style={{ minHeight: '170px' }}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <AppImage
              src={galeriItems?.[1]?.src}
              alt={galeriItems?.[1]?.alt}
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 33vw"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-3 left-3">
              <span className="px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs">
                Stainless
              </span>
            </div>
          </motion.div>

          {/* Card 3 */}
          <motion.div
            className="lg:col-span-1 relative image-zoom-container rounded-2xl overflow-hidden shadow-soft border border-border"
            style={{ minHeight: '170px' }}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <AppImage
              src={galeriItems?.[2]?.src}
              alt={galeriItems?.[2]?.alt}
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 33vw"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-3 left-3">
              <span className="px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs">
                Signage
              </span>
            </div>
          </motion.div>

          {/* Card 4 - col-span-1 */}
          <motion.div
            className="lg:col-span-1 relative image-zoom-container rounded-2xl overflow-hidden shadow-soft border border-border"
            style={{ minHeight: '180px' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <AppImage
              src={galeriItems?.[3]?.src}
              alt={galeriItems?.[3]?.alt}
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 33vw"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-3 left-3">
              <span className="px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs">
                CNC Router
              </span>
            </div>
          </motion.div>

          {/* Card 5 - col-span-2 to fill last row */}
          <motion.div
            className="lg:col-span-2 relative image-zoom-container rounded-2xl overflow-hidden shadow-soft border border-border"
            style={{ minHeight: '180px' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <AppImage
              src={galeriItems?.[4]?.src}
              alt={galeriItems?.[4]?.alt}
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 66vw"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-3 left-3">
              <span className="px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs">
                Fiber Laser
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
